import { useState, useEffect } from 'react';
import { UserCog, Plus, Edit, Loader2, Save, X, Search, KeyRound, Building2, Trash2 } from 'lucide-react';
import { userAPI } from '../services/api';
import { toast } from 'sonner';
import Paginator from '../components/ui/Paginator';

const roleColors = { Admin: 'bg-rose-100 text-rose-800', 'IT Manager': 'bg-indigo-100 text-indigo-800', Approver: 'bg-emerald-100 text-emerald-800', 'IT Support': 'bg-blue-100 text-blue-800', 'End User': 'bg-slate-100 text-slate-700' };

export function UserRolesPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', username: '', first_name: '', last_name: '', password: '', role: '', department: '', phone: '', location: '', is_active: true, mfa_setup_required: false });
  const [resetModal, setResetModal] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [showDeptSection, setShowDeptSection] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');

  const fetch = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      const [uRes, rRes, dRes] = await Promise.all([userAPI.list(params), userAPI.listRoles(), userAPI.listDepartments()]);
      setUsers(uRes.data?.results || uRes.data || []);
      setTotal(uRes.data?.count || 0);
      setRoles(rRes.data?.results || rRes.data || []);
      setDepartments(dRes.data?.results || dRes.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); // eslint-disable-next-line
  }, [page, pageSize]);

  const filtered = users;  // Search is now server-side via params

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return; // guard against double-submit
    try {
      if (editing) {
        if (!form.first_name?.trim()) { toast.error('First Name is required'); return; }
        if (!form.last_name?.trim())  { toast.error('Last Name is required'); return; }
        if (!form.role)               { toast.error('Role is required'); return; }
        setSaving(true);
        await userAPI.update(editing, { first_name: form.first_name, last_name: form.last_name, role: form.role || null, department: form.department || null, phone: form.phone, location: form.location, is_active: form.is_active });
        toast.success('User updated');
      } else {
        const missing = [];
        if (!form.email?.trim())      missing.push('Email');
        if (!form.username?.trim())   missing.push('Username');
        if (!form.first_name?.trim()) missing.push('First Name');
        if (!form.last_name?.trim())  missing.push('Last Name');
        if (!form.role)               missing.push('Role');
        if (missing.length) { toast.error(`Required fields missing: ${missing.join(', ')}`); return; }
        if (form.password && form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        const data = { email: form.email, username: form.username, first_name: form.first_name, last_name: form.last_name, role: form.role };
        if (form.password) data.password = form.password;
        if (form.department) data.department = form.department;
        if (form.phone) data.phone = form.phone;
        if (form.mfa_setup_required) data.mfa_setup_required = true;
        setSaving(true);
        await userAPI.create(data);
        toast.success('User created — welcome email with credentials has been sent');
      }
      setShowForm(false); setEditing(null); fetch();
    } catch (err) {
      const detail = err.response?.data?.email?.[0] || err.response?.data?.username?.[0] || err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed';
      toast.error(detail.includes('already exists') ? 'A user with this email already exists.' : detail);
    } finally { setSaving(false); }
  };

  const startEdit = (u) => {
    setForm({ email: u.email, username: u.username, first_name: u.first_name, last_name: u.last_name, password: '', role: u.role || '', department: u.department || '', phone: u.phone || '', location: u.location || '', is_active: u.is_active });
    setEditing(u.id); setShowForm(true);
  };

  const handleResetPassword = async () => {
    if (!resetPwd || resetPwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await userAPI.resetPassword(resetModal.userId, resetPwd);
      toast.success(`Password reset for ${resetModal.email}`);
      setResetModal(null); setResetPwd('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) { toast.error('Department name required'); return; }
    try {
      await userAPI.createDepartment({ name: newDeptName });
      toast.success(`Department "${newDeptName}" created`);
      setNewDeptName('');
      fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDeleteDepartment = async (id, name) => {
    if (!window.confirm(`Delete department "${name}"?`)) return;
    try { await userAPI.deleteDepartment(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed — department may be in use'); }
  };

  const roleCounts = {};
  users.forEach(u => { const r = u.role_name || 'No Role'; roleCounts[r] = (roleCounts[r] || 0) + 1; });

  return (
    <div data-testid="user-roles-page">
      {/* Password Reset Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-1">Reset Password</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Set new password for <span className="font-medium text-slate-700 dark:text-slate-300">{resetModal.email}</span></p>
            <input type="password" value={resetPwd} onChange={e => setResetPwd(e.target.value)} placeholder="New password (min 8 characters)" className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white mb-4 focus:ring-2 focus:ring-sky-500 outline-none" autoFocus />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setResetModal(null); setResetPwd(''); }} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-sm">Cancel</button>
              <button onClick={handleResetPassword} className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm hover:bg-sky-600">Reset Password</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <UserCog className="w-6 h-6 text-slate-600 dark:text-slate-400" /> User Management
        </h2>
        <div className="flex gap-2">
          <button onClick={() => setShowDeptSection(!showDeptSection)} className="bg-white dark:bg-slate-800 border dark:border-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-slate-50">
            <Building2 className="w-4 h-4" /> Departments
          </button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm({ email: '', username: '', first_name: '', last_name: '', password: '', role: '', department: '', phone: '', location: '', is_active: true }); }}
            className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600"><Plus className="w-4 h-4" /> Add User</button>
        </div>
      </div>

      {/* Department Management Panel */}
      {showDeptSection && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-5 mb-6">
          <h3 className="font-semibold dark:text-white mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-sky-500" /> Departments</h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {departments.map(d => (
              <div key={d.id} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700 border dark:border-slate-600 px-3 py-2 rounded-lg">
                <span className="text-sm font-medium dark:text-white">{d.name}</span>
                <button onClick={() => handleDeleteDepartment(d.id, d.name)} className="text-slate-400 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {departments.length === 0 && <span className="text-sm text-slate-400">No departments</span>}
          </div>
          <div className="flex gap-2">
            <input value={newDeptName} onChange={e => setNewDeptName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddDepartment()} placeholder="New department name" className="border dark:border-slate-700 rounded-lg px-3 py-2 text-sm dark:bg-slate-900 dark:text-white w-64" />
            <button onClick={handleAddDepartment} className="bg-sky-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-sky-600"><Plus className="w-4 h-4 inline mr-1" />Add</button>
          </div>
        </div>
      )}

      {/* Role Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {roles.map(role => {
          const label = role.display_name || role.name;
          return (
            <div key={role.id} className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 shadow-sm">
              <span className={`${roleColors[label] || 'bg-slate-100 text-slate-700'} px-2.5 py-1 rounded-lg text-xs font-semibold`}>{label}</span>
              <div className="text-2xl font-bold text-slate-800 dark:text-white mt-2">{roleCounts[label] || 0}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">users</div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} onKeyDown={e => e.key === 'Enter' && fetch()} placeholder="Search users..." className="w-full pl-9 pr-4 py-2.5 border dark:border-slate-700 rounded-xl text-sm dark:bg-slate-800 dark:text-white" />
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold dark:text-white">{editing ? 'Edit User' : 'Create New User'}</h3>
            <span className="text-xs text-slate-400"><span className="text-rose-500">*</span> Required fields</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {!editing && (
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Email <span className="text-rose-500">*</span></label>
                <input type="email" placeholder="user@company.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className={`w-full border rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white ${!form.email && 'border-slate-300 dark:border-slate-600'}`} />
              </div>
            )}
            {!editing && (
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Username <span className="text-rose-500">*</span></label>
                <input placeholder="e.g. john_doe" value={form.username} onChange={e => setForm({...form, username: e.target.value})}
                  className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
              </div>
            )}
            {!editing && (
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">
                  Password <span className="text-slate-400 font-normal">(leave blank to auto-generate &amp; email)</span>
                </label>
                <input type="password" placeholder="Optional — leave blank to auto-generate" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">First Name <span className="text-rose-500">*</span></label>
              <input placeholder="First name" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Last Name <span className="text-rose-500">*</span></label>
              <input placeholder="Last name" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Role <span className="text-rose-500">*</span></label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white ${!form.role ? 'border-slate-300 dark:border-slate-600' : 'border-sky-500 dark:border-sky-600'}`}>
                <option value="">Select Role *</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.display_name || r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Department</label>
              <select value={form.department} onChange={e => setForm({...form, department: e.target.value})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
                <option value="">Select Department</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Phone <span className="text-slate-400 text-xs">(optional)</span></label>
              <input placeholder="Phone number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            </div>
            {editing && (
              <div className="flex items-center">
                <label className="flex items-center gap-2 text-sm dark:text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="rounded" /> Active
                </label>
              </div>
            )}
            {!editing && (
              <div className="col-span-full">
                <div
                  onClick={() => setForm({...form, mfa_setup_required: !form.mfa_setup_required})}
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    form.mfa_setup_required
                      ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900'
                  }`}
                  data-testid="require-mfa-toggle"
                >
                  <input type="checkbox" checked={form.mfa_setup_required} onChange={() => {}} className="mt-0.5 rounded accent-sky-500" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800 dark:text-white">Require MFA on first login</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      User must set up both <strong>Authenticator App</strong> and <strong>Email OTP</strong> before accessing the system
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 hover:bg-sky-600 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm"><X className="w-4 h-4 inline mr-1" />Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Name</th>
                <th className="px-4 py-3 text-left font-semibold">Email</th>
                <th className="px-4 py-3 text-center font-semibold">Role</th>
                <th className="px-4 py-3 text-left font-semibold">Department</th>
                <th className="px-4 py-3 text-left font-semibold">Teams</th>
                <th className="px-4 py-3 text-center font-semibold">Online</th>
                <th className="px-4 py-3 text-left font-semibold">Last Login</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700 text-sm">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {/* Online indicator dot on avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300">
                          {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                        </div>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800 ${u.is_online ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                          title={u.is_online ? 'Currently online' : u.last_active ? `Last seen ${new Date(u.last_active).toLocaleString()}` : 'Never logged in'} />
                      </div>
                      <div>
                        <div className="font-medium dark:text-white">{u.full_name || `${u.first_name} ${u.last_name}`}</div>
                        <div className="text-xs text-slate-400">@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{u.email}</td>
                  <td className="px-4 py-3 text-center"><span className={`${roleColors[u.role_name] || 'bg-slate-100 text-slate-700'} px-2 py-1 rounded-full text-xs font-semibold`}>{u.role_name || 'No Role'}</span></td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{u.department_name || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.team_memberships?.map(tm => (<span key={tm.team_id} className="bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 px-2 py-0.5 rounded text-xs">{tm.team_name} ({tm.role})</span>))}
                      {(!u.team_memberships || u.team_memberships.length === 0) && <span className="text-slate-400 text-xs">No team</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.is_online ? (
                      <span className="inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 rounded-full text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Online
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-slate-400 text-xs" title={u.last_active ? `Last seen ${new Date(u.last_active).toLocaleString()}` : 'Never logged in'}>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                        {u.last_active ? 'Offline' : 'Never'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                    {u.last_login
                      ? <div>
                          <div>{new Date(u.last_login).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                          <div className="text-slate-400">{new Date(u.last_login).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                        </div>
                      : <span className="text-slate-300 dark:text-slate-600 italic">Never</span>}
                  </td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-1 rounded-full text-xs ${u.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => startEdit(u)} className="text-slate-400 hover:text-sky-600 p-1" title="Edit user"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => { setResetModal({ userId: u.id, email: u.email }); setResetPwd(''); }} className="text-slate-400 hover:text-amber-600 p-1" title="Reset password"><KeyRound className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No users found</td></tr>}
            </tbody>
          </table>
          <Paginator total={total} page={page} pageSize={pageSize} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
        </div>
      )}
    </div>
  );
}
